import * as yup from 'yup';

export interface OrganicRegistrationFormValues {
  email: string;
  emailConfirmation: string;
  password: string;
  policyNumber: string;
  dateOfBirth: string;
  driversLicense: string;
  zipCode: string;
  policyType: 'auto' | 'home' | null;
  firstName: string;
  lastName?: string;
}

const validationMessages = {
  required: 'Required field',
  email: 'Enter a valid email address',
  emailMatch: 'Email address does not match',
  password: 'Enter a valid password',
  policyNumber: 'Incorrect policy number format',
  policyNumberLength:
    'Incorrect policy number format. Add complete policy number.',
  zipCode: 'Enter a valid 5-digit zip code.',
  driversLicense: "Doesn't appear to be a valid drivers license number",
  dateOfBirth: 'Enter a valid date of birth (MM/DD/YYYY)',
};

export const organicRegistrationValidationSchema = yup.object({
  lastName: yup.string().required(validationMessages.required),
  email: yup
    .string()
    .required(validationMessages.required)
    .min(2, validationMessages.email)
    .email(validationMessages.email)
    .matches(/\.[A-Za-z]{2,}$/, validationMessages.email),
  emailConfirmation: yup
    .string()
    .required(validationMessages.required)
    .min(2, validationMessages.email)
    .email(validationMessages.email)
    .matches(/\.[A-Za-z]{2,}$/, validationMessages.email)
    .test('matches-email', validationMessages.emailMatch, (value, context) => {
      const emailField = context.parent.email?.toLowerCase();
      return value?.toLowerCase() === emailField;
    }),
  password: yup
    .string()
    .required(validationMessages.required)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)([^\s]){8,64}$/,
      validationMessages.password
    ),
});

export const organicPolicyDetailsValidationSchema = yup.object({
  policyType: yup.string().nullable(),
  policyNumber: yup
    .string()
    .when('policyType', {
      is: 'auto',
      then: yup
        .string()
        .required(validationMessages.required)
        .matches(/^[a-zA-Z0-9]*$/, validationMessages.policyNumber),
    })
    .when('policyType', {
      is: 'home',
      then: yup
        .string()
        .required(validationMessages.required)
        .min(5, validationMessages.policyNumberLength)
        .matches(/^[a-zA-Z0-9]*$/, validationMessages.policyNumber),
    }),
  driversLicense: yup.string().when('policyType', {
    is: 'auto',
    then: yup.string().required(validationMessages.driversLicense),
  }),
  zipCode: yup
    .string()
    .required(validationMessages.zipCode)
    .min(5, validationMessages.zipCode)
    .max(5, validationMessages.zipCode),
  dateOfBirth: yup.string().when('policyType', {
    is: 'home',
    then: yup
      .string()
      .required(validationMessages.dateOfBirth)
      .length(10, validationMessages.dateOfBirth)
      .test('isValidDate', validationMessages.dateOfBirth, (value) => {
        if (!value) return false;
        // Convert the value to a Date object
        const date = new Date(value);

        // Check if the date is a valid date
        if (isNaN(date.getTime())) return false;

        // Check if the date is more than 16 years ago
        const sixteenYearsAgo = new Date();
        sixteenYearsAgo.setFullYear(sixteenYearsAgo.getFullYear() - 16);

        return date < sixteenYearsAgo;
      }),
  }),
});
